import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { useAccountGeneralInfoForm } from '@/hooks/useAccountGeneralInfoForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { AccountFormData } from '@/types';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { AccountGeneralInfoFormFields } from './AccountGeneralInfoFormFields';

interface AccountGeneralInfoFormProps {
  token: string;
  onSubmit?: (data: AccountFormData) => void;
  onError?: (error: Error) => void;
  onChange?: (data: Partial<AccountFormData>) => void;
  onValidationTrigger?: (isValid: boolean) => void;
  className?: string;
  initialData?: Partial<AccountFormData>;
}

export interface AccountGeneralInfoFormRef {
  validateForm: () => Promise<boolean>;
  submitForm: () => Promise<void>;
  triggerValidation: () => Promise<boolean>;
}

export const AccountGeneralInfoForm = forwardRef<
  AccountGeneralInfoFormRef,
  AccountGeneralInfoFormProps
>(
  (
    {
      token,
      onSubmit,
      onError,
      onChange,
      onValidationTrigger,
      className,
      initialData,
    },
    ref
  ) => {
    // Translation hook
    const { t: tForms } = useFormsTranslation();

    // Initialize form with custom hook
    const {
      form,
      onSubmit: handleSubmit,
      submitError,
      formOptions,
      isLoading,
    } = useAccountGeneralInfoForm(token, tForms, initialData);

    // Real-time form data changes for tab persistence
    useEffect(() => {
      if (onChange) {
        const subscription = form.watch(data => {
          // Send partial form data back to parent for persistence
          onChange(data as Partial<AccountFormData>);
        });
        return () => subscription.unsubscribe();
      }
    }, [form, onChange]);

    // Clear canton when country changes away from Switzerland
    useEffect(() => {
      const subscription = form.watch((value, { name }) => {
        if (name === 'country' && value.country !== 'CH') {
          const currentCanton = form.getValues('canton');
          if (currentCanton) {
            form.setValue('canton', '');
          }
        }
      });
      return () => subscription.unsubscribe();
    }, [form]);

    // Handle form submission with callbacks
    const handleFormSubmit = useCallback(
      async (data: AccountFormData) => {
        try {
          await handleSubmit(data);
          onSubmit?.(data);
        } catch (error) {
          console.error('Form submission error:', error);
          onError?.(error as Error);
        }
      },
      [handleSubmit, onSubmit, onError]
    );

    // Expose form methods via ref
    useImperativeHandle(
      ref,
      () => ({
        validateForm: async () => {
          const isValid = await form.trigger();
          onValidationTrigger?.(isValid);
          return isValid;
        },
        submitForm: async () => {
          const isValid = await form.trigger();
          if (isValid) {
            const data = form.getValues();
            await handleFormSubmit(data);
          }
        },
        triggerValidation: async () => {
          const isValid = await form.trigger();
          onValidationTrigger?.(isValid);
          return isValid;
        },
      }),
      [form, onValidationTrigger, handleFormSubmit]
    );

    return (
      <div className={className}>
        {submitError && (
          <Alert variant='destructive' className='mb-6'>
            <AlertDescription>
              {submitError.message || tForms('errors.updateFailed')}
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form
            id='account-general-form'
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className='space-y-6'
          >
            <AccountGeneralInfoFormFields
              control={form.control}
              errors={form.formState.errors}
              formOptions={formOptions}
              isLoading={isLoading}
              tForms={tForms}
              watchedCountry={form.watch('country')}
            />
          </form>
        </Form>
      </div>
    );
  }
);

AccountGeneralInfoForm.displayName = 'AccountGeneralInfoForm';

export default AccountGeneralInfoForm;
